/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-multi-comp */
import { lazy, Suspense } from "react";

// Constants
import { GET_POPUP_TYPES } from "constants/popupConfig";

// Hooks
import { useBreakpoint } from "hooks/useBreakpoint";
import useGetPopupState from "hooks/useGetPopupState";
import useGetSubPopupState from "hooks/useGetSubPopupState";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useGetPageState from "../hooks/useGetPageState";
import { useTogglePopup } from "../hooks/useTogglePopup";
import * as authSelectors from "../redux/auth/selectors";

const LazyPaymentModalContainer = lazy(
  () => import("containers/organisms/Payment/PaymentModalContainer"),
);
const LazyPaymentWindowContainer = lazy(
  () => import("containers/organisms/Payment/PaymentWindowContainer"),
);
const LazyDepositResultModalContainer = lazy(
  () => import("containers/organisms/DepositResultModalContainer"),
);
const LazyDepositResultWindowContainer = lazy(
  () => import("containers/organisms/DepositResultWindowContainer"),
);
const LazyWithdrawResultModalContainer = lazy(
  () => import("containers/organisms/WithdrawResultModalContainer"),
);
const LazyWithdrawResultWindowContainer = lazy(
  () => import("containers/organisms/WithdrawResultWindowContainer"),
);
const LazyBonusDescriptionModalContainer = lazy(
  () => import("containers/organisms/BonusDescriptionModalContainer"),
);
const LazyCancelBonusModalContainer = lazy(
  () => import("containers/organisms/CancelBonusModalContainer"),
);
const LazyLoginModal = lazy(() => import("components/organisms/LoginModal"));
const LazyLoginSheet = lazy(() => import("components/organisms/LoginSheet"));
const LazyRegistrationModal = lazy(
  () => import("components/organisms/RegistrationModal"),
);
const LazyRegistrationSheet = lazy(
  () => import("components/organisms/RegistrationSheet"),
);
const LazyWheelWinModal = lazy(
  () => import("components/organisms/WheelWinModal"),
);
const LazyWheelWinSheet = lazy(
  () => import("components/organisms/WheelWinSheet"),
);
const LazyLandingRegistrationModal = lazy(
  () => import("components/organisms/LandingRegistrationModal"),
);
const LazyLandingRegistrationSheet = lazy(
  () => import("components/organisms/LandingRegistrationSheet"),
);
const LazyChangePasswordModal = lazy(
  () => import("components/organisms/ChangePasswordModal"),
);
const LazyChangePasswordSheet = lazy(
  () => import("components/molecules/ChangePasswordSheet"),
);
const LazyChangeEmailModal = lazy(
  () => import("components/molecules/ChangeEmailModal"),
);
const LazyChangeEmailSheet = lazy(
  () => import("components/molecules/ChangeEmailSheet"),
);
const LazyLogoutConfirmationSheet = lazy(
  () => import("components/molecules/LogoutConfirmationSheet"),
);
const LazyLogoutConfirmationModal = lazy(
  () => import("components/molecules/LogoutConfirmationModal"),
);
const LazyRecoveryModal = lazy(
  () => import("components/organisms/RecoveryModal"),
);
const LazyRecoveryPasswordSheet = lazy(
  () => import("components/molecules/RecoveryPasswordSheet"),
);
const LazyRecoveryCompleteModal = lazy(
  () => import("components/organisms/RecoveryCompleteModal"),
);
const LazyRecoveryPasswordChangeSheet = lazy(
  () => import("components/molecules/RecoveryPasswordChangeSheet"),
);
const LazyLanguageSelectModal = lazy(
  () => import("components/organisms/LanguageSelectModal"),
);
const LazyLanguageSelectWindow = lazy(
  () => import("components/organisms/LanguageSelectWindow"),
);
// eslint-disable-next-line unused-imports/no-unused-vars
// const LazyAviatorLandingModal = lazy(
//   () => import("components/organisms/AviatorLandingModal")
// );
const LazyProviderSheet = lazy(
  () => import("components/organisms/ProviderSheet"),
);
const LazyProviderSortSheet = lazy(
  () => import("components/organisms/ProviderSortSheet"),
);
const LazyGameSelectWindow = lazy(
  () => import("components/organisms/GameSelectWindow"),
);
const LazyPersonalDataBlockWindow = lazy(
  () => import("components/organisms/PersonalDataWindow"),
);
const LazyPasswordWindow = lazy(
  () => import("components/organisms/PasswordWindow"),
);
const LazyBankListSheet = lazy(
  () => import("components/molecules/BankListSheet"),
);
const LazyCountriesListSheet = lazy(
  () => import("components/molecules/CountriesListSheet"),
);
const LazySideMenuWindow = lazy(
  () => import("components/molecules/SideMenuWindow"),
);
const LazyInviteFriendWindow = lazy(
  () => import("components/organisms/InviteFriendWindow"),
);
const LazyInviteFriendSheet = lazy(
  () => import("components/molecules/InviteFriendSheet"),
);
const LazyInviteFriendNullSheet = lazy(
  () => import("components/molecules/InviteFriendNullSheet"),
);
const LazyInviteFriendModal = lazy(
  () => import("components/molecules/InviteFriendModal"),
);
const LazyInviteFriendNullModal = lazy(
  () => import("components/molecules/InviteFriendNullModal"),
);
const LazyTransactionSortSheet = lazy(
  () => import("components/organisms/TransactionSortSheet"),
);
const LazyShareSheet = lazy(() => import("components/organisms/ShareSheet"));
const LazyPromocodeModal = lazy(
  () => import("components/organisms/PromocodeModal"),
);
const LazyPromocodeSheet = lazy(
  () => import("components/organisms/PromocodeSheet"),
);

// Notifications Modals
const LazyWelcomeBonusModal = lazy(
  () => import("components/organisms/NotificationModals/WelcomeBonusModal"),
);
const LazyWelcomeBonusSheet = lazy(
  () => import("components/organisms/NotificationSheets/WelcomeBonusSheet"),
);
const LazyBonusDepositNotificationModal = lazy(
  () =>
    import("components/organisms/NotificationModals/FirstDepositBonusModal"),
);
const LazyBonusDepositNotificationSheet = lazy(
  () =>
    import("components/organisms/NotificationSheets/FirstDepositBonusSheet"),
);

const LazyBonusAddedModal = lazy(
  () => import("components/organisms/NotificationModals/BonusAddedModal"),
);
const LazyBonusAddedSheet = lazy(
  () => import("components/organisms/NotificationSheets/BonusAddedSheet"),
);

const LazyIndependenceDayCounterModal = lazy(
  () =>
    import(
      "components/organisms/NotificationModals/IndependenceDayCounterModal"
    ),
);
const LazyIndependenceDayCounterSheet = lazy(
  () =>
    import(
      "components/organisms/NotificationSheets/IndependenceDayCounterSheet"
    ),
);

const LazyWageringCounterModal = lazy(
  () => import("components/organisms/NotificationModals/WageringCounterModal"),
);
const LazyWageringCounterSheet = lazy(
  () => import("components/organisms/NotificationSheets/WageringCounterSheet"),
);

const LazyIndependenceDayFinalModal = lazy(
  () =>
    import("components/organisms/NotificationModals/IndependenceDayFinalModal"),
);
const LazyIndependenceDayFinalSheet = lazy(
  () =>
    import("components/organisms/NotificationSheets/IndependenceDayFinalSheet"),
);

const LazyIndependenceDayModal = lazy(
  () => import("components/organisms/NotificationModals/IndependenceDayModal"),
);
const LazyIndependenceDaySheet = lazy(
  () => import("components/organisms/NotificationSheets/IndependenceDaySheet"),
);

const LazyMoneyBonusModal = lazy(
  () => import("components/organisms/NotificationModals/MoneyBonusModal"),
);
const LazyMoneyBonusSheet = lazy(
  () => import("components/organisms/NotificationSheets/MoneyBonusSheet"),
);

const LazyСashoutConfirmModal = lazy(
  () => import("components/organisms/NotificationModals/СashoutConfirmModal"),
);
const LazyСashoutConfirmSheet = lazy(
  () => import("components/organisms/NotificationSheets/СashoutConfirmSheet"),
);

const LazyFirstFailedModal = lazy(
  () => import("components/organisms/NotificationModals/FirstFailedModal"),
);
const LazyFirstFailedSheet = lazy(
  () => import("components/organisms/NotificationSheets/FirstFailedSheet"),
);

const LazySecondFailedModal = lazy(
  () => import("components/organisms/NotificationModals/SecondFailedModal"),
);
const LazySecondFailedSheet = lazy(
  () => import("components/organisms/NotificationSheets/SecondFailedSheet"),
);

const LazyThirdFailedModal = lazy(
  () => import("components/organisms/NotificationModals/ThirdFailedModal"),
);
const LazyThirdFailedSheet = lazy(
  () => import("components/organisms/NotificationSheets/ThirdFailedSheet"),
);

const LazyUtilizationModal = lazy(
  () => import("components/organisms/NotificationModals/UtilizationModal"),
);
const LazyUtilizationSheet = lazy(
  () => import("components/organisms/NotificationSheets/UtilizationSheet"),
);
const LazyFreespinAvailableModal = lazy(
  () =>
    import("components/organisms/NotificationModals/FreespinAvailableModal"),
);
const LazyFreespinAvailableSheet = lazy(
  () =>
    import("components/organisms/NotificationSheets/FreespinAvailableSheet"),
);
const LazyFreespinAssignedModal = lazy(
  () => import("components/organisms/NotificationModals/FreespinAssignedModal"),
);
const LazyFreespinAssignedSheet = lazy(
  () => import("components/organisms/NotificationSheets/FreespinAssignedSheet"),
);
const LazyFreespinAccuredModal = lazy(
  () => import("components/organisms/NotificationModals/FreespinAccuredModal"),
);
const LazyFreespinAccuredSheet = lazy(
  () => import("components/organisms/NotificationSheets/FreespinAccuredSheet"),
);
const LazyPasswordConfirmModal = lazy(
  () => import("components/organisms/ConfirmPasswordModal"),
);
const LazyPasswordConfirmSheet = lazy(
  () => import("components/organisms/ConfirmPasswordSheet"),
);
const LazyLoyaltyRewardModalContainer = lazy(
  () => import("containers/organisms/LoyaltyRewardModalContainer"),
);
const LazyLoyaltyRewardBottomSheetContainer = lazy(
  () => import("containers/organisms/LoyaltyRewardBottomSheetContainer"),
);
const LazyLoyaltyBoosterInfoModalContainer = lazy(
  () => import("containers/organisms/LoyaltyBoosterInfoModalContainer"),
);
const LazyLoyaltyBoosterActivateModalContainer = lazy(
  () => import("containers/organisms/LoyaltyBoosterActivateModalContainer"),
);
const LazyLoyaltyBoosterInfoSheetContainer = lazy(
  () => import("containers/organisms/LoyaltyBoosterInfoSheetContainer"),
);
const LazyLoyaltyBoosterActivateSheetContainer = lazy(
  () => import("containers/organisms/LoyaltyBoosterActivateSheetContainer"),
);
const LazyLoyaltyOnboardingWindow = lazy(
  () => import("containers/organisms/LoyaltyOnboardingMobileContainer"),
);
const LazyLoyaltyRollbackModalContainer = lazy(
  () => import("containers/organisms/LoyaltyRollbackModalContainer"),
);
const LazyLoyaltyRollbackBottomSheetContainer = lazy(
  () => import("containers/organisms/LoyaltyRollbackBottomSheetContainer"),
);
const LazyLoyaltyNotificationModal = lazy(
  () => import("components/organisms/LoyaltyNotificationModal"),
);
const LazyLoyaltyNotificationBottomSheet = lazy(
  () => import("components/organisms/LoyaltyNotificationBottomSheet"),
);
const LazyPhoneCountriesSheet = lazy(
  () => import("components/organisms/PhoneCountriesSheet"),
);
const LazyCollectPhoneModal = lazy(
  () => import("components/organisms/CollectPhoneModal"),
);
const LazyCollectPhoneSheet = lazy(
  () => import("components/organisms/CollectPhoneSheet"),
);
const LazyFortuneWheelModal = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelModal/FortuneWheelModal"
    ),
);
const LazyFortuneWheelSheet = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelModal/FortuneWheelSheet"
    ),
);
const LazyFortuneWheelWinModal = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelWinModal/FortuneWheelWinModal"
    ),
);
const LazyFortuneWheelWinSheet = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelWinModal/FortuneWheelWinSheet"
    ),
);
const LazyFortuneWheelConditionsModal = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelConditionsModal/FortuneWheelConditionsModal"
    ),
);
const LazyFortuneWheelConditionsSheet = lazy(
  () =>
    import(
      "components/pages/Profile/FortuneWheelPage/components/FortuneWheelConditionsModal/FortuneWheelConditionsSheet"
    ),
);

const popups = {
  [GET_POPUP_TYPES.popup.deposit]: {
    desktop: LazyPaymentModalContainer,
    mobile: LazyPaymentWindowContainer,
  },
  [GET_POPUP_TYPES.popup.withdraw]: {
    desktop: LazyPaymentModalContainer,
    mobile: LazyPaymentWindowContainer,
  },
  [GET_POPUP_TYPES.popup.signIn]: {
    desktop: LazyLoginModal,
    mobile: LazyLoginSheet,
  },
  [GET_POPUP_TYPES.popup.signUp]: {
    desktop: LazyRegistrationModal,
    mobile: LazyRegistrationSheet,
  },
  [GET_POPUP_TYPES.popup.recovery]: {
    desktop: LazyRecoveryModal,
    mobile: LazyRecoveryPasswordSheet,
  },
  [GET_POPUP_TYPES.popup.recoveryComplete]: {
    desktop: LazyRecoveryCompleteModal,
    mobile: LazyRecoveryPasswordChangeSheet,
  },
  [GET_POPUP_TYPES.popup.winWheel]: {
    desktop: LazyWheelWinModal,
    mobile: LazyWheelWinSheet,
  },
  [GET_POPUP_TYPES.popup.regWheel]: {
    desktop: LazyLandingRegistrationModal,
    mobile: LazyLandingRegistrationSheet,
  },
  [GET_POPUP_TYPES.popup.changePassword]: {
    desktop: LazyChangePasswordModal,
    mobile: LazyChangePasswordSheet,
  },
  [GET_POPUP_TYPES.popup.changeEmail]: {
    desktop: LazyChangeEmailModal,
    mobile: LazyChangeEmailSheet,
  },
  [GET_POPUP_TYPES.popup.depositResult]: {
    desktop: LazyDepositResultModalContainer,
    mobile: LazyDepositResultWindowContainer,
  },
  [GET_POPUP_TYPES.popup.withdrawResult]: {
    desktop: LazyWithdrawResultModalContainer,
    mobile: LazyWithdrawResultWindowContainer,
  },
  [GET_POPUP_TYPES.popup.bonus]: {
    desktop: LazyBonusDescriptionModalContainer,
    mobile: LazyBonusDescriptionModalContainer,
  },
  [GET_POPUP_TYPES.popup.cancelBonus]: {
    desktop: LazyCancelBonusModalContainer,
    mobile: LazyCancelBonusModalContainer,
  },
  [GET_POPUP_TYPES.popup.changeLanguage]: {
    desktop: LazyLanguageSelectModal,
    mobile: LazyLanguageSelectWindow,
  },
  [GET_POPUP_TYPES.popup.aviatorReg]: {
    desktop: LazyLandingRegistrationModal,
    mobile: LazyLandingRegistrationSheet,
  },
  [GET_POPUP_TYPES.popup.logoutConfirm]: {
    desktop: LazyLogoutConfirmationModal,
    mobile: LazyLogoutConfirmationSheet,
  },
  [GET_POPUP_TYPES.popup.provider]: {
    desktop: null,
    mobile: LazyProviderSheet,
  },
  [GET_POPUP_TYPES.popup.sortProvider]: {
    desktop: null,
    mobile: LazyProviderSortSheet,
  },
  [GET_POPUP_TYPES.popup.gameSearch]: {
    desktop: null,
    mobile: LazyGameSelectWindow,
  },
  [GET_POPUP_TYPES.subpopup.selectBank]: {
    desktop: null,
    mobile: LazyBankListSheet,
  },
  [GET_POPUP_TYPES.popup.sideMenu]: {
    desktop: null,
    mobile: LazySideMenuWindow,
  },
  [GET_POPUP_TYPES.popup.countries]: {
    desktop: null,
    mobile: LazyCountriesListSheet,
  },
  [GET_POPUP_TYPES.popup.welcomeBonusNotification]: {
    desktop: LazyWelcomeBonusModal,
    mobile: LazyWelcomeBonusSheet,
  },
  [GET_POPUP_TYPES.popup.firstDepositNotification]: {
    desktop: LazyBonusDepositNotificationModal,
    mobile: LazyBonusDepositNotificationSheet,
  },
  [GET_POPUP_TYPES.popup.bonusAddedNotification]: {
    desktop: LazyBonusAddedModal,
    mobile: LazyBonusAddedSheet,
  },
  [GET_POPUP_TYPES.popup.independenceDayCounterNotification]: {
    desktop: LazyIndependenceDayCounterModal,
    mobile: LazyIndependenceDayCounterSheet,
  },
  [GET_POPUP_TYPES.popup.wageringCounterNotification]: {
    desktop: LazyWageringCounterModal,
    mobile: LazyWageringCounterSheet,
  },
  [GET_POPUP_TYPES.popup.independenceDayFinalNotification]: {
    desktop: LazyIndependenceDayFinalModal,
    mobile: LazyIndependenceDayFinalSheet,
  },
  [GET_POPUP_TYPES.popup.independenceDayNotification]: {
    desktop: LazyIndependenceDayModal,
    mobile: LazyIndependenceDaySheet,
  },
  [GET_POPUP_TYPES.popup.moneyBonusNotification]: {
    desktop: LazyMoneyBonusModal,
    mobile: LazyMoneyBonusSheet,
  },
  [GET_POPUP_TYPES.popup.cashoutConfirmNotification]: {
    desktop: LazyСashoutConfirmModal,
    mobile: LazyСashoutConfirmSheet,
  },

  [GET_POPUP_TYPES.popup.firstFailedDeposit]: {
    desktop: LazyFirstFailedModal,
    mobile: LazyFirstFailedSheet,
  },
  [GET_POPUP_TYPES.popup.secondFailedDeposit]: {
    desktop: LazySecondFailedModal,
    mobile: LazySecondFailedSheet,
  },
  [GET_POPUP_TYPES.popup.thirdFailedDeposit]: {
    desktop: LazyThirdFailedModal,
    mobile: LazyThirdFailedSheet,
  },
  [GET_POPUP_TYPES.popup.utilizationBalance]: {
    desktop: LazyUtilizationModal,
    mobile: LazyUtilizationSheet,
  },
  [GET_POPUP_TYPES.popup.invite]: {
    desktop: LazyInviteFriendModal,
    mobile: LazyInviteFriendSheet,
  },
  [GET_POPUP_TYPES.popup.inviteNull]: {
    desktop: LazyInviteFriendNullModal,
    mobile: LazyInviteFriendNullSheet,
  },
  [GET_POPUP_TYPES.popup.sortTransaction]: {
    desktop: null,
    mobile: LazyTransactionSortSheet,
  },
  [GET_POPUP_TYPES.popup.share]: {
    desktop: null,
    mobile: LazyShareSheet,
  },
  [GET_POPUP_TYPES.popup.coinStrikeReg]: {
    desktop: LazyLandingRegistrationModal,
    mobile: LazyLandingRegistrationSheet,
  },
  [GET_POPUP_TYPES.popup.freespinAvailableNotification]: {
    desktop: LazyFreespinAvailableModal,
    mobile: LazyFreespinAvailableSheet,
  },
  [GET_POPUP_TYPES.popup.freespinAssignedNotification]: {
    desktop: LazyFreespinAssignedModal,
    mobile: LazyFreespinAssignedSheet,
  },
  [GET_POPUP_TYPES.popup.freespinAccuredNotification]: {
    desktop: LazyFreespinAccuredModal,
    mobile: LazyFreespinAccuredSheet,
  },
  [GET_POPUP_TYPES.popup.promocodeApply]: {
    desktop: LazyPromocodeModal,
    mobile: LazyPromocodeSheet,
  },
  [GET_POPUP_TYPES.popup.passwordRegistration]: {
    desktop: LazyPasswordConfirmModal,
    mobile: LazyPasswordConfirmSheet,
  },
  [GET_POPUP_TYPES.popup.rewardsLevel]: {
    desktop: LazyLoyaltyRewardModalContainer,
    mobile: LazyLoyaltyRewardBottomSheetContainer,
  },
  [GET_POPUP_TYPES.popup.rewardsLevelCompleted]: {
    desktop: LazyLoyaltyRewardModalContainer,
    mobile: LazyLoyaltyRewardBottomSheetContainer,
  },
  [GET_POPUP_TYPES.popup.rewardsRank]: {
    desktop: LazyLoyaltyRewardModalContainer,
    mobile: LazyLoyaltyRewardBottomSheetContainer,
  },
  [GET_POPUP_TYPES.popup.rewardsRankCompleted]: {
    desktop: LazyLoyaltyRewardModalContainer,
    mobile: LazyLoyaltyRewardBottomSheetContainer,
  },
  [GET_POPUP_TYPES.popup.boosterInfo]: {
    desktop: LazyLoyaltyBoosterInfoModalContainer,
    mobile: LazyLoyaltyBoosterInfoSheetContainer,
  },
  [GET_POPUP_TYPES.popup.boosterActivate]: {
    desktop: LazyLoyaltyBoosterActivateModalContainer,
    mobile: LazyLoyaltyBoosterActivateSheetContainer,
  },
  [GET_POPUP_TYPES.popup.loyaltyOnboarding]: {
    desktop: null,
    mobile: LazyLoyaltyOnboardingWindow,
  },
  [GET_POPUP_TYPES.popup.rollbackInfo]: {
    desktop: LazyLoyaltyRollbackModalContainer,
    mobile: LazyLoyaltyRollbackBottomSheetContainer,
  },
  [GET_POPUP_TYPES.popup.cashbackAssign]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.rankUp]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.levelRankUp]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.boosterAvailable]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.daysWithoutProgress]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.manyDaysWithoutProgress]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.levelDanger]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.rollback]: {
    desktop: LazyLoyaltyNotificationModal,
    mobile: LazyLoyaltyNotificationBottomSheet,
  },
  [GET_POPUP_TYPES.popup.phoneCountries]: {
    desktop: null,
    mobile: LazyPhoneCountriesSheet,
  },
  [GET_POPUP_TYPES.popup.collectPhone]: {
    desktop: LazyCollectPhoneModal,
    mobile: LazyCollectPhoneSheet,
  },
  [GET_POPUP_TYPES.popup.fortuneWheel]: {
    desktop: LazyFortuneWheelModal,
    mobile: LazyFortuneWheelSheet,
  },
  [GET_POPUP_TYPES.popup.fortuneWheelWin]: {
    desktop: LazyFortuneWheelWinModal,
    mobile: LazyFortuneWheelWinSheet,
  },
  [GET_POPUP_TYPES.popup.fortuneWheelConditions]: {
    desktop: LazyFortuneWheelConditionsModal,
    mobile: LazyFortuneWheelConditionsSheet,
  },
};

const pages = {
  [GET_POPUP_TYPES.page.profile]: {
    desktop: null,
    mobile: LazyPersonalDataBlockWindow,
  },
  [GET_POPUP_TYPES.page.password]: {
    desktop: null,
    mobile: LazyPasswordWindow,
  },
  [GET_POPUP_TYPES.page.invite]: {
    desktop: null,
    mobile: LazyInviteFriendWindow,
  },
};

export const PopupManager = () => {
  const { mountedPopup, isOpened } = useGetPopupState();
  const { isDesktop } = useBreakpoint();
  const location = useLocation();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const { openPopup, closePopup } = useTogglePopup();

  // Parse the query parameters
  const params = new URLSearchParams(location.search);
  const rParam = params.get("r");
  const isSignUpParam = location.search.includes(GET_POPUP_TYPES.popup.signUp);

  if (rParam && !isSignUpParam && !isAuthenticated) {
    location.search = `?r=${rParam}`;
    openPopup(GET_POPUP_TYPES.popup.signUp);
  }

  if (isSignUpParam && isAuthenticated) {
    closePopup();
  }

  if (!mountedPopup || !popups[mountedPopup]) {
    // eslint-disable-next-line no-console
    // console.error(`Invalid popup: ${mountedPopup}`);
    return null;
  }

  const popupConfig = popups[mountedPopup];
  const Component = isDesktop ? popupConfig.desktop : popupConfig.mobile;

  if (!Component) {
    // eslint-disable-next-line no-console
    console.error(
      `Popup config for ${mountedPopup} is missing mobile or desktop component`,
    );
    return null;
  }

  return (
    <Suspense fallback="Loading">
      <Component isOpened={isOpened} />
    </Suspense>
  );
};

export const PageManager = () => {
  const { mountedPopup, isOpened } = useGetPageState();
  const { isDesktop } = useBreakpoint();

  const Component = mountedPopup
    ? isDesktop
      ? pages[mountedPopup].desktop
      : pages[mountedPopup].mobile
    : null;

  if (!Component) {
    return null;
  }

  return (
    <Suspense fallback="Loading">
      <Component isOpened={isOpened} />
    </Suspense>
  );
};

export const SubPopupManager = () => {
  const { mountedPopup, isOpened } = useGetSubPopupState();
  const { isDesktop } = useBreakpoint();

  const Component = mountedPopup
    ? isDesktop
      ? popups[mountedPopup].desktop
      : popups[mountedPopup].mobile
    : null;

  if (!Component) {
    return null;
  }

  return (
    <Suspense fallback="Loading">
      <Component isOpened={isOpened} />
    </Suspense>
  );
};
